/**
 * Extract a user-friendly error message from any caught error
 * (axios, fetch, plain Error, string). NEVER returns the bare
 * "Request failed with status code N" axios default — that's a
 * lousy UX. Returns the backend's FastAPI `detail` field first,
 * then an http-status-aware default, then the supplied fallback.
 *
 *   try { ... } catch (e) {
 *     toast.error(errMessage(e, "Could not save score"));
 *   }
 */
export function errMessage(err, fallback = "Something went wrong.") {
  // FastAPI returns { detail: "..." } for HTTPException
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    // Pydantic 422 validation errors → "field: msg"
    const first = detail[0];
    if (first?.msg) return `${(first.loc || []).join(".")}: ${first.msg}`;
  }

  const status = err?.response?.status;
  if (status) {
    if (status === 404) return fallback + " (not found)";
    if (status === 401) return "You need to be signed in for that.";
    if (status === 403) return "You don't have access to that.";
    if (status === 408 || status === 504) return "Request timed out. Try again.";
    if (status === 429) return "Too many requests — please slow down.";
    if (status === 502 || status === 503) return "Our backend is briefly unavailable. Try again in a moment.";
    if (status >= 500) return "Backend error — try again or contact support.";
  }

  // Network-layer failures
  if (err?.code === "ERR_NETWORK") return "Network unreachable — check your connection.";
  if (err?.code === "ECONNABORTED") return "Request timed out.";

  // Plain Error thrown by our own code (e.g. "Generation failed")
  const m = err?.message;
  if (typeof m === "string" && m.trim() && !m.startsWith("Request failed with status code")) {
    return m;
  }

  return fallback;
}
